import clsx from 'clsx';
import type { ReactNode } from 'react';

/**
 * toncenter.com section heading (DESIGN.md §3): an accent eyebrow label in
 * sans caps above a large medium-weight heading. `hero` is the page-level
 * variant (52px desktop), `section` the in-page one (40px); both drop to
 * 32px at ≤767px.
 */
interface SectionHeadingProps {
  label: ReactNode;
  title: ReactNode;
  id?: string;
  variant?: 'hero' | 'section';
  /** Overrides the accent label colour (e.g. a status tone in the hero). */
  labelClassName?: string;
}

export function SectionHeading({
  label,
  title,
  id,
  variant = 'section',
  labelClassName,
}: SectionHeadingProps) {
  const Heading = variant === 'hero' ? 'h1' : 'h2';
  return (
    <div className="flex min-w-0 flex-col items-start gap-4 min-[768px]:gap-[18px]">
      <p
        className={clsx(
          'm-0 text-[14px] font-semibold uppercase leading-none min-[768px]:text-[16px]',
          labelClassName ?? 'text-accent',
        )}
      >
        {label}
      </p>
      <Heading
        id={id}
        className={clsx(
          'm-0 break-words text-[32px] font-medium leading-[1.05] tracking-display text-ink-1',
          variant === 'hero'
            ? 'min-[768px]:text-[52px]'
            : 'min-[768px]:text-[40px] min-[768px]:leading-[1.23]',
        )}
      >
        {title}
      </Heading>
    </div>
  );
}
